import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {Label} from '@/components/ui/label'
import {NativeSelect} from '@/components/native-select'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {Textarea} from '@/components/ui/textarea'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useChoques,
  useResolverChoque,
  type Choque,
  type ChoqueSalida,
} from '@/hooks/empresa/cumplimiento/use-cumplimiento'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'

/** `persona_retirada` no la elige nadie (la escribe el retiro), así que no está en el selector. */
const SALIDAS: ChoqueSalida[] = ['reasignar', 'concepto_nuevo', 'autorizado_h12', 'no_es_choque']

/** Mismo tope que `ResolverChoqueWrite.motivo`: el 422 de pydantic no está en `KNOWN_ERRORS`. */
const MOTIVO_MAX = 500

/** D12, E10-H14. Quien no custodia (§4) solo recibe el conteo: el servidor decide qué esconder. */
export function ChoquesSection({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const {data, isLoading, isError, error} = useChoques(businessId, employeeId)
  // Escribir una salida es un acto legal con nombre y fecha: respeta el modo dios de solo lectura.
  const godReadOnly = useGodModeReadOnly()
  const [abierto, setAbierto] = useState<Choque | null>(null)
  if (isLoading) return <Skeleton className="h-20 w-full" data-testid="choques-cargando" />
  if (isError)
    return (
      <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="choques-error">
        {t('choque.errorCarga')} {translateApiError(error)}
      </p>
    )
  // Sin choques no se monta: «no hay choque» ya lo dice la franja de la hoja.
  if (!data || (data.choques.length === 0 && data.abiertos === 0)) return null
  return (
    <section className="space-y-2" data-testid="choques-section">
      <h3 className="text-sm font-medium">{t('choque.titulo')}</h3>
      {data.detalle_reducido ? (
        <p className="text-xs text-muted-foreground" data-testid="choques-sin-detalle">
          {t('choque.sinDetalle', {count: data.abiertos})}
        </p>
      ) : (
        <ul className="space-y-3">
          {data.choques.map((choque) => (
            <ChoqueFila
              key={choque.id}
              choque={choque}
              soloLectura={godReadOnly}
              onResolver={() => setAbierto(choque)}
            />
          ))}
        </ul>
      )}
      <ResolverDialog
        businessId={businessId}
        employeeId={employeeId}
        choque={abierto}
        onCerrar={() => setAbierto(null)}
      />
    </section>
  )
}

function ChoqueFila({choque, soloLectura, onResolver}: {choque: Choque; soloLectura: boolean; onResolver: () => void}) {
  const {t} = useTranslation()
  return (
    <li className="space-y-1" data-testid={`choque-${choque.id}`}>
      <p className="text-sm">
        {t('choque.nombrado', {
          restriccion: choque.restriccion_texto,
          condicion: choque.condicion_codigo
            ? t(`condicionCargo.codigo.${choque.condicion_codigo}`)
            : choque.forma_reconocida,
          cargo: choque.cargo_nombre ?? '',
        })}
      </p>
      <p className="text-xs text-muted-foreground">
        {t('choque.formaReconocida', {forma: choque.forma_reconocida, version: choque.catalogo_version})}
      </p>
      {choque.salida ? (
        <p className="text-xs text-muted-foreground" data-testid={`choque-resuelto-${choque.id}`}>
          {t('choque.resuelto', {
            nombre: choque.salida_por ?? '',
            fecha: choque.salida_en ? formatDateOnly(choque.salida_en.slice(0, 10)) : '',
            motivo: choque.salida_motivo ?? t(`choque.salida.${choque.salida}`),
          })}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Tag tone={choque.deshabilita ? 'red' : 'neutral'}>
            {choque.deshabilita ? t('choque.deshabilita') : t('choque.informa')}
          </Tag>
          <Button
            variant="outline"
            size="sm"
            disabled={soloLectura}
            onClick={onResolver}
            data-testid={`choque-resolver-${choque.id}`}>
            {t('choque.resolver')}
          </Button>
        </div>
      )}
    </li>
  )
}

/** Kairos nunca levanta el choque solo. `no_es_choque` cierra sin tocar restricción ni concepto. */
function ResolverDialog({
  businessId,
  employeeId,
  choque,
  onCerrar,
}: {
  businessId: string
  employeeId: string
  choque: Choque | null
  onCerrar: () => void
}) {
  const {t} = useTranslation()
  const [salida, setSalida] = useState<ChoqueSalida>('reasignar')
  const [motivo, setMotivo] = useState('')
  const [hallazgo, setHallazgo] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const resolver = useResolverChoque(businessId, employeeId)
  if (!choque) return null
  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('choque.resolver')}</DialogTitle>
          <DialogDescription>{t('choque.nadieLoLevanta')}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-3"
          onSubmit={(evento) => {
            evento.preventDefault()
            const problema = validateRequired(motivo.trim())
            setHallazgo(problema ?? undefined)
            if (problema) return
            setError(null)
            resolver.mutate(
              {choqueId: choque.id, salida, motivo: motivo.trim()},
              {onSuccess: onCerrar, onError: (err) => setError(translateApiError(err))}
            )
          }}>
          <div className="space-y-1.5">
            <Label htmlFor="choque-salida">{t('choque.resolver')}</Label>
            <NativeSelect
              id="choque-salida"
              data-testid="choque-salida"
              value={salida}
              onChange={(evento: React.ChangeEvent<HTMLSelectElement>) =>
                setSalida(evento.target.value as ChoqueSalida)
              }>
              {SALIDAS.map((opcion) => (
                <option key={opcion} value={opcion}>
                  {t(`choque.salida.${opcion}`)}
                </option>
              ))}
            </NativeSelect>
            {salida === 'no_es_choque' && (
              <p className="text-xs text-muted-foreground" data-testid="choque-no-es-choque-aviso">
                {t('choque.noEsChoqueAviso')}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="choque-motivo">{t('choque.motivo')}</Label>
            <Textarea
              id="choque-motivo"
              maxLength={MOTIVO_MAX}
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              onBlur={() => setHallazgo(validateRequired(motivo.trim()) ?? undefined)}
              data-testid="choque-motivo"
            />
            <FieldError error={hallazgo} />
          </div>
          <FormDialogFooter
            onCancel={onCerrar}
            submitLabel={t('choque.resolver')}
            pending={resolver.isPending}
            error={error}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
